'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuthStore } from '@/hooks/use-auth';
import { useChildProfiles } from '@/hooks/use-profile';
import { useTRPC } from '@/lib/trpc/provider';
import { useMutation, useQuery } from '@tanstack/react-query';
import { formatPrice } from '@/lib/utils';
import { prorateByMeetings } from '@/lib/enrollment/meeting-proration';
import { toast } from 'sonner';
import { Loader2, ClipboardCheck, CalendarCheck, CalendarX, CheckCircle } from 'lucide-react';
import type {
  MidTermAvailability,
  ShortCourseAvailability,
  CohortCtaState,
} from '@/server/services/course.service';
import { CohortCourseCTA } from '@/components/enrollment/cohort-course-cta';
import { MidTermPreferencesForm } from '@/components/enrollment/mid-term-preferences-form';
import {
  ClassPicker,
  NONE_OF_THESE_TIMES,
  type PickableClass,
} from '@/components/enrollment/class-picker';
import { AnotherTimeRequest } from '@/components/enrollment/another-time-request';
import { TimezoneMismatchBanner } from '@/components/scheduling/timezone-mismatch-banner';
import { PaymentMethodSelect, usePaymentMethod } from '@/components/payments/PaymentMethodSelect';
import { redirectToCheckout } from '@/lib/payments/redirect-to-checkout';

export type CourseEnrollmentCTAProps = {
  slug: string;
  courseId: string;
  courseTitle?: string;
  pricePerTermCents: number;
  /**
   * ⚠️ REQUIRED, not optional — `null` means "no class running", and that is
   * now a state the CTA NAMES to the parent. While this was optional, a call
   * site that simply forgot it rendered "no class running" for a course that
   * had one. That is not hypothetical: the public course page omitted
   * `shortCourse` for exactly that reason, so a short course fell through to
   * the wrong branch and its request lost its section targeting.
   */
  midTermAvailability: MidTermAvailability | null;
  /** Whether any term is currently in enrollment_open status */
  hasOpenEnrollmentTerm: boolean;
  /** Short-course (dated section) availability — wins over every other branch. */
  shortCourse: ShortCourseAvailability | null;
  /** Overseas Enrolment Task 20 — non-null only when the VIEWING SCOPE's
   *  program is a cohort program (AC21/J10). */
  cohortCta?: CohortCtaState | null;
};

export function CourseEnrollmentCTA({
  slug,
  courseId,
  courseTitle,
  pricePerTermCents,
  midTermAvailability,
  hasOpenEnrollmentTerm,
  shortCourse,
  cohortCta,
}: CourseEnrollmentCTAProps) {
  const t = useTranslations('public.enrollment.cta');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);

  if (!isAuthenticated) {
    return (
      <Button
        asChild
        className="min-h-[44px] min-w-[44px] w-full bg-eifo-orange hover:bg-eifo-orange/85 text-[var(--eifo-primary)] font-semibold"
      >
        <Link href={`/register?redirect=/courses/${slug}`}>{t('registerToEnroll')}</Link>
      </Button>
    );
  }

  if (user?.role !== 'parent') {
    return (
      <Button
        className="min-h-[44px] min-w-[44px] w-full bg-eifo-orange hover:bg-eifo-orange/85 text-[var(--eifo-primary)] font-semibold"
        disabled
        title={t('parentOnly')}
      >
        {t('enrollNow')}
      </Button>
    );
  }

  // Overseas Enrolment (Task 20/AC21): a cohort program never opens a term, so
  // every branch below — short-course pricing, mid-term backfill, the
  // open-enrollment redirect, the mid-term request form — is inapplicable. The
  // only actions that exist are join the waitlist, try a class, or join a class
  // that is already running.
  //
  // ⚠️ Set per COURSE and resolved against the VIEWING SCOPE's program, so a US
  // Online course renders exactly as it did (J10/H7).
  if (cohortCta) {
    return <CohortCourseCTA courseId={courseId} courseTitle={courseTitle} state={cohortCta} />;
  }

  // Short course (dated section): wins over the ENTIRE decision tree below —
  // the direct-backfill checkout would charge the wrong (full-term prorated)
  // price with no admin review, and an overlapping open-enrollment window
  // would capture short-course parents into the preferences/matching flow.
  if (shortCourse) {
    if (shortCourse.hasEnded) {
      // Never fall through to the legacy branches — the most-recent-term
      // request path would resurrect the wrong-pricing bug for this course.
      return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center space-y-2">
          <CalendarX className="h-6 w-6 text-gray-400 mx-auto" />
          <p className="text-sm font-medium text-gray-600">{t('runEndedTitle')}</p>
          <p className="text-xs text-gray-500">
            {t('runEndedBody')}
          </p>
        </div>
      );
    }
    return (
      <MidTermPreferencesForm
        courseId={courseId}
        courseTitle={courseTitle ?? t('thisCourse')}
        slug={slug}
        sectionId={shortCourse.sectionId}
        shortCourse={shortCourse}
      />
    );
  }

  // Mid-term backfill: instant checkout for prorated price.
  //
  // ⚠️ ONLY the `join` state may take money. A running course that is full
  // (`waitlist`) or nearly over (`ending`) still appears in the availability map
  // now — it used to drop out of it entirely — so it must fall through to the
  // request form below rather than render a checkout button with no seat behind
  // it. The card and the form say which case it is; see `MidTermBadge`.
  if (midTermAvailability?.state === 'join') {
    return (
      <MidTermBackfillForm
        courseId={courseId}
        pricePerTermCents={pricePerTermCents}
        midTermAvailability={midTermAvailability}
      />
    );
  }

  // Open enrollment running: route into the unified open-enrollment flow
  // (preferences → matching → confirm & pay) so the request is visible to
  // admins and included in term scheduling.
  if (hasOpenEnrollmentTerm) {
    return (
      <div className="rounded-lg border border-[#1e306e]/20 bg-[#1e306e]/5 p-4 space-y-3">
        <div className="flex items-center gap-2 text-[#1e306e] font-semibold">
          <CalendarCheck className="h-5 w-5" />
          {t('openTitle')}
        </div>
        <p className="text-sm text-[#334155]">
          {t('openBody')}
        </p>
        <Button
          asChild
          className="min-h-[44px] min-w-[44px] w-full bg-eifo-orange hover:bg-eifo-orange/85 text-[var(--eifo-primary)] font-semibold"
        >
          <Link href="/enrollment/preferences">{t('enrollUpcoming')}</Link>
        </Button>
      </div>
    );
  }

  // No open enrollment: submit enrollment request for admin review.
  //
  // The two refusal states get a line of copy above the form saying WHY the
  // join-and-pay option is not there. Without it the option simply vanished and
  // the parent filled in a form with no idea what had happened — which is how a
  // full class and a finishing one became indistinguishable.
  // At this point `midTermAvailability` is exactly one of null | waitlist |
  // ending: `cohortCta`, `shortCourse`, the `join` state and the open-enrolment
  // redirect have all returned above. So `null` here provably means "this
  // course has no class running right now" — the largest case by far (31 of 48
  // catalog courses on 9 Sep 2026) and, until now, the only one with no words
  // at all. A parent filled in an availability form with nothing behind it.
  return (
    <div className="space-y-3">
      {midTermAvailability === null && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-sm font-semibold text-gray-700">{t('noClassTitle')}</p>
          <p className="text-xs text-gray-600 mt-0.5">{t('noClassBody')}</p>
        </div>
      )}
      {midTermAvailability?.state === 'waitlist' && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
          <p className="text-sm font-semibold text-amber-900">{t('waitlistTitle')}</p>
          <p className="text-xs text-amber-800 mt-0.5">{t('waitlistBody')}</p>
        </div>
      )}
      {midTermAvailability?.state === 'ending' && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-sm font-semibold text-gray-700">{t('endingTitle')}</p>
          <p className="text-xs text-gray-600 mt-0.5">
            {t('endingBody', { n: String(midTermAvailability.remainingSessions) })}
          </p>
        </div>
      )}
      <MidTermPreferencesForm
        courseId={courseId}
        courseTitle={courseTitle ?? t('thisCourse')}
        slug={slug}
        // "None of these times" is offered on a FULL course only — never on
        // `ending`: there is no carry-over, so nothing later this term to wait
        // for. The term is the one whose classes the form will show.
        anotherTimeTermId={
          midTermAvailability?.state === 'waitlist' ? midTermAvailability.termId : undefined
        }
      />
    </div>
  );
}

function MidTermBackfillForm({
  courseId,
  pricePerTermCents,
  midTermAvailability,
}: {
  courseId: string;
  pricePerTermCents: number;
  midTermAvailability: MidTermAvailability;
}) {
  const t = useTranslations('public.enrollment.cta');
  const tf = useTranslations('public.enrollment.form');
  const tCourses = useTranslations('public.courses');
  const tc = useTranslations('common');
  const [selectedChild, setSelectedChild] = useState('');
  const [selectedSectionId, setSelectedSectionId] = useState('');
  // "None of these times" — the request `AnotherTimeRequest` sends, not a seat.
  const [anotherTimePending, setAnotherTimePending] = useState(false);
  const [anotherTimeSubmitted, setAnotherTimeSubmitted] = useState(false);
  const { children, isLoading: childrenLoading } = useChildProfiles();

  const trpc = useTRPC();

  const { method, setMethod } = usePaymentMethod();
  const [couponCode, setCouponCode] = useState('');

  // The classes this course is running. Short runs are filtered out: they are
  // priced per session through their own flow, and selling one here at the
  // full-term prorated price is precisely what the short-course branch above
  // exists to prevent.
  // D6: the SAME term the pay button is about to charge for. Without it the
  // picker resolves the term on its own and the two can disagree.
  const { data: classData } = useQuery(
    trpc.enrollment.getClassesForCourse.queryOptions({
      courseId,
      termId: midTermAvailability.termId,
    })
  );
  const joinable = ((classData?.classes ?? []) as PickableClass[]).filter(
    (c) => !c.isShortRun && !c.hasUnassignedTeacher && !c.isFull
  );

  // A class the parent chose can fill under them between the first load and a
  // refetch. Left alone, the radio vanishes from the list while the selection
  // survives, so the pay button stays live and submits a class no longer on
  // offer — an unexplained error on a form that looks complete.
  //
  // "None of these times" is exempt: it is not a class, so it cannot vanish
  // from the list, and resetting it would throw away the times the family is
  // ticking every time the list refetches.
  const wantsAnotherTime = selectedSectionId === NONE_OF_THESE_TIMES;
  const stillOffered = joinable.some((c) => c.sectionId === selectedSectionId);
  if (selectedSectionId && !wantsAnotherTime && classData && !stillOffered) {
    setSelectedSectionId('');
  }

  // The price is THE PICKED CLASS's: its own meetings left, through the same
  // rule checkout charges by. Two classes of one course on different weekdays
  // can have different numbers left. Until one is picked, the course's "from"
  // price stands in (the button is disabled then anyway).
  const selectedClass = joinable.find((c) => c.sectionId === selectedSectionId);
  const selectedMeetings = selectedClass?.meetings ?? null;
  const priceCents = selectedMeetings
    ? prorateByMeetings(selectedMeetings, pricePerTermCents)
    : midTermAvailability.proratedPriceCents;

  const backfillMutation = useMutation(
    trpc.enrollment.createBackfillCheckout.mutationOptions({
      onSuccess: (data) => {
        redirectToCheckout(data.redirect);
      },
      onError: (error) => {
        toast.error(error.message, { duration: 8000 });
      },
    })
  );

  // Placement gate — per selected child, so a child who has passed (or is in
  // band) is no longer blocked.
  const { data: eligibility } = useQuery({
    ...trpc.placement.getEnrollmentEligibility.queryOptions({
      childProfileId: selectedChild,
      courseId,
    }),
    enabled: !!selectedChild,
  });
  const block = selectedChild && eligibility && !eligibility.ok ? eligibility : null;
  const needsPlacement =
    !!block &&
    (block.reason === 'placement_required' ||
      block.reason === 'placement_not_passed');

  const handleBackfillEnroll = () => {
    if (!selectedChild) {
      toast.error(t('selectChildToast'));
      return;
    }
    // The sentinel is UI-only — it is never a section, so it never reaches
    // checkout, even though the pay button is not rendered while it is chosen.
    if (!selectedSectionId || selectedSectionId === NONE_OF_THESE_TIMES) {
      toast.error(tf('selectClassToast'));
      return;
    }
    if (block) {
      toast.error(block.message, { duration: 6000 });
      return;
    }
    backfillMutation.mutate({
      courseId,
      studentProfileId: selectedChild,
      termId: midTermAvailability.termId,
      // WHICH class. Without it the family pays, is activated, and appears on
      // no weekly schedule at all — the defect this rail carried until now.
      sectionId: selectedSectionId,
      method,
      couponCode: method !== 'card' && couponCode.trim() ? couponCode.trim() : undefined,
    });
  };

  // The another-time request went in: the whole form gives way to the
  // confirmation, whatever the class list does next.
  if (anotherTimeSubmitted) {
    return (
      <div
        className="rounded-lg border border-green-200 bg-green-50 p-4 text-center space-y-2"
        data-testid="another-time-submitted"
      >
        <CheckCircle className="h-8 w-8 text-green-600 mx-auto" />
        <p className="text-green-800 font-medium">{tf('anotherTimeSubmittedTitle')}</p>
        <p className="text-green-700 text-sm">{tf('anotherTimeSubmittedBody')}</p>
      </div>
    );
  }

  // ⚠️ The CTA renders on the COURSE-level `join` state
  // (`availableSeats > 0`), which is entirely compatible with every individual
  // class being full, teacherless, or a short run. Returning EARLY, rather than
  // hiding the button inside the form, is deliberate: it makes "a pay button
  // with nothing to pay for" unreachable instead of one JSX edit away.
  //
  // Gated on `classData` so the loading pass does not flash the dead end.
  if (classData && joinable.length === 0) {
    return (
      <div
        className="rounded-lg border border-gray-200 bg-gray-50 p-3"
        data-testid="no-joinable-class"
      >
        <p className="text-sm font-semibold text-gray-700">{tf('noOpenClassTitle')}</p>
        <p className="text-xs text-gray-600 mt-0.5">{tf('noOpenClassBody')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Select value={selectedChild} onValueChange={setSelectedChild}>
        <SelectTrigger className="w-full min-h-[44px]" aria-label={t('selectChildAria')}>
          <SelectValue placeholder={childrenLoading ? tc('loading') : t('selectAChild')} />
        </SelectTrigger>
        <SelectContent>
          {children.map((child) => (
            <SelectItem key={child.id} value={child.id}>
              {child.first_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {needsPlacement && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
          <div className="flex items-center gap-2 text-amber-800 font-medium mb-1">
            <ClipboardCheck className="h-4 w-4" />
            {t('placementRequired')}
          </div>
          <p className="text-amber-700 text-xs">
            {block?.message ?? t('placementFallback')}
          </p>
          <Link
            href={`/parent/placement/${courseId}`}
            className="mt-2 inline-block text-xs text-[var(--eifo-primary)] font-semibold underline"
          >
            {t('takePlacement')}
          </Link>
        </div>
      )}

      {block && !needsPlacement && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          {block.message}
        </div>
      )}

      {/* Above the class list: the times below are in the profile's zone. */}
      <TimezoneMismatchBanner />

      <ClassPicker
        classes={joinable}
        value={selectedSectionId}
        onChange={setSelectedSectionId}
        mode="book"
        allowNone
        disabled={backfillMutation.isPending || anotherTimePending}
      />

      {/* "None of these times" takes no seat, so nothing here takes money:
        * the payment selector, promo field and pay button all give way to the
        * day × time-of-day question. */}
      {wantsAnotherTime ? (
        <AnotherTimeRequest
          courseId={courseId}
          termId={midTermAvailability.termId}
          childProfileId={selectedChild}
          blocked={!!block}
          onSubmitted={() => setAnotherTimeSubmitted(true)}
          onPendingChange={setAnotherTimePending}
        />
      ) : (
        <>
          {selectedMeetings && (
            <p className="text-sm text-muted-foreground" data-testid="selected-class-sessions">
              {tCourses('sessionsRemaining', {
                remaining: selectedMeetings.remaining,
                total: selectedMeetings.total,
              })}
            </p>
          )}
          {selectedChild && !block && (
            <PaymentMethodSelect value={method} onChange={setMethod} disabled={backfillMutation.isPending} />
          )}
          {selectedChild && !block && method !== 'card' && (
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              disabled={backfillMutation.isPending}
              placeholder={t('promoCodePlaceholder')}
              className="w-full min-h-[44px] rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#fc800a]/30"
            />
          )}

          <Button
            onClick={handleBackfillEnroll}
            disabled={!selectedChild || !selectedSectionId || backfillMutation.isPending || !!block}
            className="min-h-[44px] min-w-[44px] w-full bg-[var(--eifo-secondary)] hover:bg-[#e57309] text-[var(--eifo-primary)] font-semibold"
          >
            {backfillMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('processing')}
              </>
            ) : (
              t('joinMidTerm', { price: formatPrice(priceCents) })
            )}
          </Button>
        </>
      )}
    </div>
  );
}
